import {
  Component,
  DestroyRef,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { Router } from '@angular/router';

import { AppSettings } from '../../app-settings';
import { Marker } from '../../interfaces/marker.interface';
import { OrderStore } from '../../order-store';
import { ApiService } from '../../services/api.service';

@Component({
  selector: 'app-live-tracking',
  imports: [GoogleMapsModule],
  templateUrl: './live-tracking.html',
  styleUrl: './live-tracking.scss',
})
export class LiveTracking {
  private readonly router = inject(Router);
  private readonly apiService = inject(ApiService);
  private readonly store = inject(OrderStore);
  private readonly destroyRef = inject(DestroyRef);

  private readonly map = viewChild.required(GoogleMap);

  readonly options: google.maps.MapOptions = {
    zoomControl: true,
    disableDefaultUI: false,
    mapTypeControl: false,
    fullscreenControl: false,
    disableDoubleClickZoom: true,
    scrollwheel: true,
    streetViewControl: false,
  };

  readonly markers = signal<Marker[] | undefined>(undefined);
  readonly center = signal<google.maps.LatLngLiteral>(
    AppSettings.settings['default'].latlng,
  );
  readonly mapReady = signal(false);

  constructor() {
    this.apiService.getOrderTrackingLocation();
    const interval = setInterval(
      () => this.apiService.getOrderTrackingLocation(),
      10000,
    );
    this.destroyRef.onDestroy(() => clearInterval(interval));

    effect(() => {
      const tracking = this.store.tracking();
      const dropOffLocation = { latitude: '25.186688', longitude: '55.297595' };
      if (tracking.packageLocation) {
        this.addMarkers({
          dropOffLocation,
          packageLocation: tracking.packageLocation,
        });
      }
    });
  }

  private addMarkers(locations: {
    dropOffLocation: { latitude: string; longitude: string };
    packageLocation: { latitude: string; longitude: string };
  }): void {
    const packageLocation = {
      lat: parseFloat(locations.packageLocation.latitude),
      lng: parseFloat(locations.packageLocation.longitude),
    };
    const dropOffLocation = {
      lat: parseFloat(locations.dropOffLocation.latitude),
      lng: parseFloat(locations.dropOffLocation.longitude),
    };
    // the backend omits coordinates until the driver's first GPS ping arrives
    if (
      Number.isNaN(packageLocation.lat) ||
      Number.isNaN(packageLocation.lng)
    ) {
      return;
    }

    // read/navigate imperatively so the effect doesn't re-track its own writes
    untracked(() => {
      const current = this.markers();
      if (!current) {
        this.markers.set([
          {
            position: dropOffLocation,
            icon: { url: 'imgs/drop-off-pin.svg' },
            title: 'Marker title ',
            options: { animation: google.maps.Animation.DROP },
          },
          {
            position: packageLocation,
            icon: {
              url: 'imgs/package.svg',
              scaledSize: new google.maps.Size(24, 30),
            },
            title: 'Marker title ',
            options: { animation: google.maps.Animation.DROP },
          },
        ]);
      } else {
        current[1].position = packageLocation;
        this.markers.set([...current]);
      }

      this.center.set(packageLocation);
      // panTo needs the underlying google.maps.Map, which loads asynchronously
      if (this.mapReady()) {
        this.map().panTo(packageLocation);
      }
    });
  }

  public onMapInitialized(): void {
    this.mapReady.set(true);
  }

  public back(): void {
    this.router.navigate(['../'], { queryParamsHandling: 'merge' });
  }
}
